import React, {
	useState,
	useCallback,
	useMemo,
	useEffect
} from 'react';
import {
	ConfigProvider,
	Descriptions,
	Flex,
} from 'antd';
import classNames from 'classnames';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import {
	EstimateStatus,
	FixtureTypes,
	FuelTypes,
	PortActionTypes,
} from '@shared/utils/constants';
import { formatCurrency } from '@shared/utils/currency';
import { formatAndRoundNumber } from '@shared/utils/formatNumber';
import { Values } from '@shared/utils/objectEnums';
import showSuccessNotification from '@client/utils/showSuccessNotification';
import Button from '@client/components/Button';
import TceChangeIndicator from '@client/components/TceChangeIndicator';
import TooltipIcon from '@client/components/TooltipIcon';
import getEstimatedVcPnlItems, { BUNKERS_BELOW_ZERO_WARNING } from '../helpers/getEstimatedVcPnlItems';
import { formatEuEtsEmissionCost } from '../helpers/formatEuEtsEmissionCost';
import { useEstimate } from '../context/EstimatorContext';
import { useCentralStore } from '../context/CentralStoreContext';
import { useEstimateChangesProvider } from '../context/EstimateChangesContext';
import { useCargoProvider } from '../context/CargoContext';
import { usePortRotationProvider } from '../context/PortRotationContext';
import { LocalPortRotation } from '../context/hooks/usePortRotationHandlers';
import styles from './styles/EstimatedPnL.module.css';

type FuelType = Values<typeof FuelTypes>;
export type ExpandedRows = {
	portCosts: boolean;
	bunkers: boolean;
	euEts: boolean;
	ukEts: boolean;
	totalDays: boolean;
};

const getMissingBunkerQuantities = (
	portRotation: Array<LocalPortRotation>
): Array<Values<typeof FuelTypes>> => {
	if (portRotation == null || portRotation.length === 0) {
		return [];
	}

	let missingBunkers: Array<Values<typeof FuelTypes>> = [];

	const firstEntry = portRotation[0];
	const departure = firstEntry.PortCallActions.find((pca) => pca.event === PortActionTypes.DEPARTURE);

	if (departure) {
		if (departure.RobBunkers.every((rb) => rb.forecastedQuantity === 0)) {
			return missingBunkers;
		}

		departure.RobBunkers.forEach((rb) => {
			if (rb.forecastedQuantity === 0) {
				missingBunkers.push(rb.fuelGrade);
			}
		});
	}

	return missingBunkers;
};

const getBunkersGoBelowZero = (
	portRotation: Array<LocalPortRotation>
): boolean => {
	if (portRotation == null || portRotation.length === 0) {
		return false;
	}

	const departure = portRotation[0].PortCallActions.find((pca) => pca.event === PortActionTypes.DEPARTURE);

	if (departure == null || departure.RobBunkers.every((rb) => rb.forecastedQuantity === 0)) {
		return false;
	}

	return portRotation.some((entry) => (
		entry.PortCallActions.some((action) => (
			action.RobBunkers.some((rb) => rb.forecastedQuantity < 0)
		))
	));
};

const EstimatedPnL = () => {
	const [expanded, setExpanded] = useState({
		portCosts: false,
		bunkers: false,
		euEts: false,
		ukEts: false,
		totalDays: false,
	});

	const { estimate } = useEstimate();
	const { portRotation } = usePortRotationProvider();
	const { hasPendingChanges, onSaveChanges } = useEstimateChangesProvider();
	const { componentDisabled } = ConfigProvider.useConfig();
	const isFixed = estimate?.status === EstimateStatus.Fixed;

	const {
		result: estimateDetails,
		cargos,
		tceDiff,
	} = useCentralStore();
	
	const { hasCargoRelet } = useCargoProvider();

	const [showTce, setShowTce] = useState(false);

	useEffect(() => {
		if (tceDiff === 0) {
			setShowTce(false);

			return;
		}

		setShowTce(true);

		const timer = setTimeout(() => setShowTce(false), 10000);

		return () => clearTimeout(timer);
	}, [tceDiff]);

	const vcPnl = estimateDetails?.result;

	const onSetTargetTce = useCallback(async (targetTce: number) => {
		onSaveChanges({ estimateAttributes: { targetTce } });
		showSuccessNotification('Target TCE updated');
	}, [onSaveChanges]);

	const onLockTce = useCallback(async () => {
		if (estimate == null || vcPnl == null) {
			return;
		}

		const newState = !estimate.isTceLocked;

		await onSaveChanges({
			estimateAttributes: {
				isTceLocked: newState,
				targetTce: newState ? estimateDetails?.result.tce : null,
			},
		});
		showSuccessNotification('TCE locked');
	}, [estimate, onSaveChanges, vcPnl]);

	const onSetTargetNetResult = useCallback(async (targetNetResult: number) => {
		onSaveChanges({ estimateAttributes: { targetNetResult } });
		showSuccessNotification('Target Net Result updated');
	}, [onSaveChanges]);

	const onLockNetResult = useCallback(async () => {
		if (estimate == null || vcPnl == null) {
			return;
		}

		const newState = !estimate.isNetResultLocked;

		await onSaveChanges({
			estimateAttributes: {
				isNetResultLocked: newState,
				targetNetResult: newState ? vcPnl.netProfit : null,
			},
		});
		showSuccessNotification(newState ? 'Net Result locked' : 'Net Result unlocked');
	}, [estimate, onSaveChanges, vcPnl]);

	const euaPriceToUse =
		estimateDetails?.euaPriceOverride ?? 
		estimateDetails?.remoteEuaPrice;

	const estimatedPnlItems = useMemo(() => {
		if (estimateDetails == null || vcPnl == null) {
			return [];
		}

		let items;

		const missingBunkerQuantities = getMissingBunkerQuantities(portRotation);
		const bunkersGoBelowZero = getBunkersGoBelowZero(portRotation);

		if (estimate?.type === FixtureTypes.SPOT) {
			items = getEstimatedVcPnlItems({
				estimate: estimateDetails,
				onSetTargetTce,
				onLockTce,
				onSetTargetNetResult,
				onLockNetResult,
				cargos,
				expanded,
				setExpanded,
				tceDiff,
				hideTceControls: isFixed || hasCargoRelet,
				hasCargoRelet,
				disabled: componentDisabled,
				missingBunkerQuantities,
				bunkersGoBelowZero,
			});
		} else {
			const updateExpanded = (key: keyof ExpandedRows) => {
				setExpanded((prev) => ({
					...prev,
					[key]: !prev[key],
				}));
			};

			const bunkerExpenses = estimateDetails.bunkerExpenses ?? {};
			items = [
				{
					label: 'Gross Hire',
					span: 3,
					children: <span data-testid="gross-hire">{formatCurrency(vcPnl.grossHire ?? 0, estimateDetails.currency)}</span>,
				},
				{
					label: 'Gross Ballast Bonus',
					span: 3,
					children: formatCurrency(vcPnl.otherRevenue ?? 0, estimateDetails.currency),
				},
				{
					label: 'Address Commission',
					span: 3,
					children: formatCurrency(vcPnl.addressCommissions ?? 0, estimateDetails.currency),
				},
				{
					label: 'Broker Commission',
					span: 3,
					children: formatCurrency(vcPnl.brokerCommissions ?? 0, estimateDetails.currency),
				},
				{
					label: 'Exp. subject to days',
					span: 3,
					children: formatCurrency(
						vcPnl.expensesSubjectToHireDays ?? 0, estimateDetails.currency,
					),
				},
				{
					label: 'Charterers Expenses',
					span: 3,
					children: formatCurrency(vcPnl.chartererExpenses ?? 0, estimateDetails.currency),
				},
				{
					label: (
						<b>Net Revenue</b>
					),
					span: 3,
					children: (
						<b>{formatCurrency(vcPnl.netRevenue ?? 0, estimateDetails.currency)}</b>
					),
				},
				{
					label: (
						<Flex justify="space-between">
							<Flex>
								Bunker Exp.
								{missingBunkerQuantities.length > 0 && (
									<TooltipIcon
										iconProps={{ style: { color: 'red' } }}
										Icon={ExclamationCircleOutlined}
									>
										Bunker quantities missing for one or more fuel grades
									</TooltipIcon>
								)}
								{bunkersGoBelowZero && (
									<TooltipIcon
										iconProps={{ style: { color: 'red' } }}
										Icon={ExclamationCircleOutlined}
										testId="bunkers-below-zero-warning"
									>
										{BUNKERS_BELOW_ZERO_WARNING}
									</TooltipIcon>
								)}
							</Flex>
							<Button type="link" size="small" onClick={() => updateExpanded('bunkers')}>
								{expanded.bunkers ? 'Hide' : 'Expand'}
							</Button>
						</Flex>
					),
					span: 3,
					children: formatCurrency(vcPnl.bunkerExpense ?? 0, estimateDetails.currency),
				},
				...(expanded.bunkers ? Object.keys(bunkerExpenses)
					.map((key) => {
						const isMissing = missingBunkerQuantities.find((f) => f === key) != null;

						return ({
							key,
							label: (
								<Flex>
									-{formatAndRoundNumber(Math.abs(bunkerExpenses[key as FuelType]?.consumption ?? 0), 0)}MT {key}
									{isMissing && (
										<TooltipIcon
											iconProps={{ style: { color: 'red' } }}
											Icon={ExclamationCircleOutlined}
										>
											Bunker quantities missing for this grade
										</TooltipIcon>
									)}
								</Flex>
							),
							span: 3,
							children: formatCurrency(
								bunkerExpenses[key as FuelType]?.expenditure ??
								(
									(bunkerExpenses[key as FuelType]?.consumption ?? 0) *
									(bunkerExpenses[key as FuelType]?.pricePerTon ?? 0)
								),
								estimateDetails.currency,
							)
						});
					}) : []),
				{
					label: (
						<Flex justify="space-between">
							EU ETS Costs
							<Button type="link" size="small" onClick={() => updateExpanded('euEts')}>
								{expanded.euEts ? 'Hide' : 'Expand'}
							</Button>
						</Flex>
					),
					span: 3,
					children: formatCurrency(vcPnl.totalEUAcost ?? 0, estimateDetails.currency),
				},
				...(expanded.euEts ? [{
					label: ` - At Sea: ${formatAndRoundNumber(vcPnl.totalEligibleEmissionsAtSea ?? 0, 0)} MT CO₂`,
					span: 3,
					children: formatEuEtsEmissionCost(
						euaPriceToUse,
						vcPnl?.factorAdjustedEligibleEmissionsAtSea,
						estimateDetails.currency,
					),
				},
				{
					label: ` - In Port: ${formatAndRoundNumber(vcPnl.totalEligibleEmissionsInPort ?? 0, 0)} MT CO₂`,
					span: 3,
					children: formatEuEtsEmissionCost(
						euaPriceToUse,
						vcPnl?.factorAdjustedEligibleEmissionsInPort,
						estimateDetails.currency,
					),
				},
				{
					label: ` - Other: ${formatAndRoundNumber(vcPnl.totalEligibleEmissionsOther ?? 0, 0)} MT CO₂`,
					span: 3,
					children: formatEuEtsEmissionCost(
						euaPriceToUse,
						vcPnl?.factorAdjustedEligibleEmissionsOther,
						estimateDetails.currency,
					),
				}] : []),
				{
					label: (
						<Flex justify="space-between">
							UK ETS Costs
							<Button
								type="link"
								size="small"
								onClick={() => updateExpanded('ukEts')}
							>
								{expanded.ukEts ? 'Hide' : 'Expand'}
							</Button>
						</Flex>
					),
					span: 3,
					children: formatCurrency(
						vcPnl.totalUKAcost ?? 0,
						estimateDetails.currency,
					),
				},
				...(expanded.ukEts ? [{
					label: ` - At Sea: ${formatAndRoundNumber(vcPnl.totalUkEligibleEmissionsAtSea ?? 0, 0)} MT CO₂`,
					span: 3,
					children: formatCurrency(
						-(vcPnl.totalUkEligibleEmissionsAtSea ?? 0) * (estimateDetails.ukaPrice ?? 0),
						estimateDetails.currency,
					),
				},
				{
					label: ` - In Port: ${formatAndRoundNumber(vcPnl.totalUkEligibleEmissionsInPort ?? 0, 0)} MT CO₂`,
					span: 3,
					children: formatCurrency(
						-(vcPnl.totalUkEligibleEmissionsInPort ?? 0) * (estimateDetails.ukaPrice ?? 0),
						estimateDetails.currency,
					),
				},
				{
					label: ` - Other: ${formatAndRoundNumber(vcPnl.totalUkEligibleEmissionsOther ?? 0, 0)} MT CO₂`,
					span: 3,
					children: formatCurrency(
						-(vcPnl.totalUkEligibleEmissionsOther ?? 0) * (estimateDetails.ukaPrice ?? 0),
						estimateDetails.currency,
					),
				}] : []),
				{
					label: 'FuelEU penalty',
					span: 3,
					children: formatCurrency(vcPnl.fuelEuPenalty ?? 0, estimateDetails.currency),
				},
				{
					label: 'TC Out Bunker gain/loss',
					span: 3,
					children: formatCurrency(0, estimateDetails.currency),
				},
				{
					label: 'Other Expenses',
					span: 3,
					children: (
						<span data-testid="other-expenses">
							{formatCurrency(vcPnl.ownersExpenses ?? 0, estimateDetails.currency)}
						</span>
					),
				},
				{
					label: (
						<b>Total Voyage Exp.</b>
					),
					span: 3,
					children: (
						<b>{formatCurrency(vcPnl.totalVoyageExpenses ?? 0, estimateDetails.currency)}</b>
					),
				},
				{
					label: 'TC Earnings',
					span: 3,
					children: formatCurrency(vcPnl.voyageProfit ?? 0, estimateDetails.currency),
				},
				{
					label: (
						<Flex justify="space-between">
							<b>Total Days</b>
							<Button type="link" size="small" onClick={() => updateExpanded('totalDays')}>
								{expanded.totalDays ? 'Hide' : 'Expand'}
							</Button>
						</Flex>
					),
					span: 3,
					children: (
						<b data-testid="total-days">{formatAndRoundNumber((vcPnl.estimatedDuration ?? 0), 2)}</b>
					),
				},
				...(expanded.totalDays ?
					[
						{
							label: '- TC Out',
							span: 3,
							children: formatAndRoundNumber((vcPnl.tcOutDays ?? 0), 2),
						},
						{
							label: '- Ballasting',
							span: 3,
							children: formatAndRoundNumber((vcPnl.tcBallastingDays ?? 0), 2),
						},
					] : []),
				{
					label: (<b>TC Earnings/day</b>),
					span: 3,
					children: (<b data-testid="tce">{formatCurrency(vcPnl.tce ?? 0, estimateDetails.currency)}</b>),
					style: { background: '#f0f5ff' }
				},
				{
					label: 'CAPEX',
					span: 3,
					children: formatCurrency(vcPnl.capex ?? 0, estimateDetails.currency),
				},
				{
					label: 'OPEX',
					span: 3,
					children: formatCurrency(vcPnl.opex ?? 0, estimateDetails.currency),
				},
				{
					label: 'Net Hire Cost',
					span: 3,
					children: formatCurrency(vcPnl.tcInExpense ?? 0, estimateDetails.currency),
				},
				{
					label: (<b>Net Result</b>),
					span: 3,
					children: (
						<div className={styles.highlight}>
							<span
								data-testid="net-result"
								className={classNames({
									[styles.green]: (vcPnl.netProfit ?? 0) > 0,
									[styles.red]: (vcPnl.netProfit ?? 0) < 0,
								})}
							>
								{formatCurrency(
									vcPnl.netProfit ?? 0,
									estimateDetails.currency,
								)}
							</span>
						</div>
					),
					style: { background: '#f0f5ff' }
				},
				{
					label: (<b>Net Result/day</b>),
					span: 3,
					children: (
						<div className={styles.highlight}>
							<span
								data-testid="net-result-per-day"
								className={classNames({
									[styles.green]: (vcPnl.netProfitPerDay ?? 0) > 0,
									[styles.red]: (vcPnl.netProfitPerDay ?? 0) < 0,
								})}
							>
								{formatCurrency(
									vcPnl.netProfitPerDay ?? 0,
									estimateDetails.currency,
								)}
							</span>
						</div>
					),
					style: { background: '#f0f5ff' }
				},
				...(vcPnl.apsTce != null ? [{
					label: 'APS TCE /day',
					span: 3,
					children: (
						<span data-testid="aps-tce">
							{formatCurrency(vcPnl.apsTce, estimateDetails.currency)}
						</span>
					),
				}] : []),
			];
		}

		return items;
	}, [
		estimateDetails,
		portRotation,
		vcPnl,
		cargos,
		expanded,
		isFixed,
		onLockTce,
		onSetTargetTce,
		onLockNetResult,
		onSetTargetNetResult,
		tceDiff,
		euaPriceToUse,
		componentDisabled,
	]);

	if (estimateDetails == null || estimateDetails?.currency == null || estimateDetails.result == null) {
		return null;
	}

	return (
		<Descriptions
			extra={
				showTce ? (
					<div
						style={{
							marginTop: 14,
							display: 'flex',
							justifyContent: 'center',
							width: '100%',
						}}
					>
						<TceChangeIndicator />
					</div>
				) : undefined
			}
			data-testid="estimated-pnl"
			bordered
			className={classNames(styles.pnl, { [styles.outdated]: hasPendingChanges })}
			size="small"
			items={estimatedPnlItems}
		/>
	);
};

export default EstimatedPnL;
